import { Config } from "@/types/nango"

/**
 * Internal frontend URL in the canonical shape: no trailing slash. Next
 * canonicalizes `/x/` to `/x` with a 308, so emitting the slash would cost a
 * redirect on every navigation. Query and hash are preserved as given.
 */
export function newFrontendURL(
  config: Config,
  path: string,
  loggedIn: boolean,
): string {
  const base = loggedIn ? config.BASE_LOGGED_IN_URL : ""
  const suffixIndex = path.search(/[?#]/)
  const pathname = suffixIndex === -1 ? path : path.slice(0, suffixIndex)
  const suffix = suffixIndex === -1 ? "" : path.slice(suffixIndex)
  const trimmed = pathname.replace(/^\/+/, "").replace(/\/+$/, "")
  return trimmed ? `${base}/${trimmed}${suffix}` : `${base || "/"}${suffix}`
}

/** Absolute, shareable frontend URL (includes scheme + host from config.BASE_URL). */
export function newAbsoluteFrontendURL(config: Config, path: string): string {
  return new URL(path, config.BASE_URL).toString()
}

export function newBackendURL(path: string) {
  if (path[0] === "/") {
    path = path.slice(1, path.length)
  }
  const qIndex = path.indexOf("?")
  const pathPart = qIndex === -1 ? path : path.slice(0, qIndex)
  const queryPart = qIndex === -1 ? "" : path.slice(qIndex)
  const normalized = pathPart.endsWith("/") ? pathPart.slice(0, -1) : pathPart
  return `http://django:8000/${normalized}/${queryPart}`
}

/**
 * Builds a redirect URL with the destination path encoded as a query parameter.
 * Handles cases where the login URL already has query parameters.
 *
 * @param loginUrl - The URL to redirect to (e.g., "/" or "/login?param=value")
 * @param destinationPath - The full destination path including query params (e.g., "/logged-in/requests?company=4")
 * @returns The complete redirect URL (e.g., "/?redirect=%2Flogged-in%2Frequests%3Fcompany%3D4")
 */
export function buildRedirectUrl(
  loginUrl: string,
  destinationPath: string,
): string {
  const separator = loginUrl.includes("?") ? "&" : "?"
  return (
    loginUrl + separator + "redirect=" + encodeURIComponent(destinationPath)
  )
}

/** Query param for login email prefill (distinct from `email`, which triggers OTP-only UI). */
export const PREFILL_EMAIL_QUERY_PARAM = "prefill_email" as const

/**
 * Reads a query parameter from a path that may include a search string
 * (e.g. `/logged-in/companies/1/simulation-preview?prefill_email=...`).
 */
export function getQueryParamFromPath(
  pathWithQuery: string,
  param: string,
): string | null {
  const qIndex = pathWithQuery.indexOf("?")
  if (qIndex === -1) return null
  return new URLSearchParams(pathWithQuery.slice(qIndex + 1)).get(param)
}
